// Сервис для работы с Яндекс.Метрикой

// Расширяем интерфейс Window для Яндекс.Метрики
declare global {
  interface Window {
    ym?: (id: number, method: string, ...args: any[]) => void;
    yandex_metrika_callbacks2?: Array<() => void>;
  }
}

const YANDEX_METRIKA_ID = 103835360;

/**
 * Проверить, загружена ли Яндекс.Метрика
 */
export const isYandexMetrikaLoaded = (): boolean => {
  return typeof window !== 'undefined' && typeof window.ym === 'function';
};

/**
 * Загрузить скрипт Яндекс.Метрики
 */
export const loadYandexMetrika = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Window is not defined'));
      return;
    }

    // Проверяем, не загружена ли уже метрика
    if (isYandexMetrikaLoaded()) {
      resolve();
      return;
    }

    try {
      // Инициализируем функцию ym
      window.ym = window.ym || function() {
        (window.ym.a = window.ym.a || []).push(arguments);
      };
      window.ym.l = Date.now();

      // Проверяем, не загружен ли уже скрипт
      const existingScript = document.querySelector('script[src*="mc.yandex.ru/metrika/tag.js"]');
      if (existingScript) {
        resolve();
        return;
      }

      // Создаем скрипт
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://mc.yandex.ru/metrika/tag.js?id=${YANDEX_METRIKA_ID}`;
      
      script.onload = () => {
        console.log('✅ Яндекс.Метрика загружена');
        resolve();
      };
      
      script.onerror = () => {
        console.error('❌ Ошибка загрузки Яндекс.Метрики');
        reject(new Error('Failed to load Yandex.Metrika'));
      };

      // Вставляем скрипт в head
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }

    } catch (error) {
      console.error('❌ Ошибка инициализации Яндекс.Метрики:', error);
      reject(error);
    }
  });
};

/**
 * Инициализировать Яндекс.Метрику
 */
export const initYandexMetrika = async (): Promise<void> => {
  try {
    await loadYandexMetrika();
    
    if (window.ym) {
      window.ym(YANDEX_METRIKA_ID, 'init', {
        ssr: true,
        webvisor: true,
        clickmap: true,
        ecommerce: "dataLayer",
        accurateTrackBounce: true,
        trackLinks: true
      });
      
      console.log('✅ Яндекс.Метрика инициализирована');
    }
  } catch (error) {
    console.error('❌ Ошибка инициализации Яндекс.Метрики:', error);
  }
};

/**
 * Отключить Яндекс.Метрику
 */
export const disableYandexMetrika = (): void => {
  if (typeof window !== 'undefined' && window.ym) {
    try {
      // Яндекс.Метрика не имеет стандартного метода disable,
      // поэтому мы переопределяем функцию на пустую
      window.ym = () => {};
      console.log('❌ Яндекс.Метрика отключена');
    } catch (error) {
      console.error('Ошибка отключения Яндекс.Метрики:', error);
    }
  }
};

/**
 * Включить Яндекс.Метрику заново
 */
export const enableYandexMetrika = async (): Promise<void> => {
  try {
    await initYandexMetrika();
  } catch (error) {
    console.error('Ошибка включения Яндекс.Метрики:', error);
  }
};

/**
 * Отправить цель в Яндекс.Метрику
 */
export const sendYandexMetrikaGoal = (goalName: string, params?: Record<string, any>): void => {
  if (isYandexMetrikaLoaded() && window.ym) {
    try {
      window.ym(YANDEX_METRIKA_ID, 'reachGoal', goalName, params);
      console.log(`📊 Цель "${goalName}" отправлена в Яндекс.Метрику`, params);
    } catch (error) {
      console.error('Ошибка отправки цели в Яндекс.Метрику:', error);
    }
  }
};

/**
 * Отправить событие в Яндекс.Метрику
 */
export const sendYandexMetrikaEvent = (action: string, params?: Record<string, any>): void => {
  if (isYandexMetrikaLoaded() && window.ym) {
    try {
      window.ym(YANDEX_METRIKA_ID, 'hit', window.location.href, {
        title: document.title,
        params: params
      });
      console.log(`📊 Событие "${action}" отправлено в Яндекс.Метрику`, params);
    } catch (error) {
      console.error('Ошибка отправки события в Яндекс.Метрику:', error);
    }
  }
};

// Экспортируем ID для использования в других местах
export { YANDEX_METRIKA_ID };
